import { useEffect, useRef, type ReactNode } from "react";
import { AccountPanel } from "../auth/AccountPanel";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HistoryIcon, OverviewIcon, SettingsIcon, TherapyIcon, TrendIcon } from "./Icons";

interface AppShellProps {
  children: ReactNode;
}

const navigation = [
  { to: "/", labelKey: "nav.overview", icon: OverviewIcon, end: true },
  { to: "/history", labelKey: "nav.history", icon: HistoryIcon, end: false },
  { to: "/trends", labelKey: "nav.trends", icon: TrendIcon, end: false, preload: () => import("../pages/TrendsPage") },
  { to: "/therapies", labelKey: "therapy.title", icon: TherapyIcon, end: false, preload: () => import("../pages/TherapiesPage") },
  { to: "/preferences", labelKey: "nav.preferences", icon: SettingsIcon, end: false },
];

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const initialRoute = useRef(true);
  const currentPage = t(routeLabelKey(location.pathname));

  useEffect(() => {
    document.title = `${currentPage} · ${t("common.appName")}`;
  }, [currentPage, t]);

  useEffect(() => {
    if (initialRoute.current) {
      initialRoute.current = false;
      return;
    }
    window.scrollTo({ top: 0, left: 0 });
    window.requestAnimationFrame(() => mainRef.current?.focus({ preventScroll: true }));
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-canvas text-ink lg:grid lg:grid-cols-[17rem_1fr]">
      <a href="#main-content" className="fixed left-4 top-4 z-50 -translate-y-20 rounded-full bg-ink px-4 py-2 text-sm font-bold text-canvas transition-transform focus:translate-y-0">{t("shell.skipToContent")}</a>
      <aside className="sticky top-0 z-40 border-b border-line bg-surface/95 shadow-[0_1px_0_color-mix(in_oklch,var(--color-ink)_4%,transparent)] backdrop-blur-xl lg:h-screen lg:border-r lg:border-b-0 lg:shadow-none">
        <div className="flex items-center px-5 py-4 lg:block lg:px-6 lg:py-7">
          <NavLink to="/" className="group flex items-center gap-3 rounded-xl focus-ring">
            <span className="grid size-10 place-items-center rounded-[0.9rem] bg-ink text-sm font-black tracking-[-0.08em] text-canvas shadow-sm ring-1 ring-inset ring-white/10">H+</span>
            <span>
              <span translate="no" className="block text-xl font-semibold tracking-[-0.03em]">{t("common.appName")}</span>
              <span className="hidden text-xs font-bold uppercase tracking-[0.12em] text-muted min-[22rem]:block">{t("common.tagline")}</span>
            </span>
          </NavLink>
        </div>
        <nav id="primary-navigation" aria-label={t("shell.primaryNavigation")} className="mt-4 hidden flex-col px-6 lg:flex">
          {navigation.map(({ to, labelKey, icon: Icon, end, preload }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onPointerEnter={preload}
              onFocus={preload}
              className={({ isActive }) => `pressable flex min-w-0 items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-bold focus-ring ${isActive ? "bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200" : "text-muted hover:bg-surface-raised hover:text-ink"}`}
            >
              {({ isActive }) => (
                <>
                  <span className={`grid size-8 shrink-0 place-items-center rounded-lg ${isActive ? "bg-teal-100 text-teal-900" : "bg-surface-raised text-muted"}`}><Icon className="size-5" /></span>
                  <span className="max-w-full truncate">{t(labelKey)}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="absolute inset-x-6 bottom-7 hidden border-t border-line pt-5 lg:block">
          <AccountPanel />
        </div>
      </aside>
      <nav aria-label={t("shell.primaryNavigation")} className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 gap-1 border-t border-line bg-surface/95 px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 shadow-[0_-0.75rem_2.5rem_color-mix(in_oklch,var(--color-ink)_10%,transparent)] backdrop-blur-xl lg:hidden">
        {navigation.map(({ to, labelKey, icon: Icon, end, preload }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onPointerEnter={preload}
            onFocus={preload}
            className={({ isActive }) => `pressable flex min-w-0 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-[0.68rem] font-bold focus-ring ${isActive ? "bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200" : "text-muted hover:bg-surface-raised hover:text-ink"}`}
          >
            {({ isActive }) => (
              <>
                <span className={`grid size-8 shrink-0 place-items-center rounded-lg ${isActive ? "bg-teal-100 text-teal-900" : "bg-surface-raised text-muted"}`}><Icon className="size-5" /></span>
                <span className="max-w-full truncate">{t(labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">{currentPage}</span>
      <main ref={mainRef} id="main-content" tabIndex={-1} className="min-w-0 px-5 pb-28 pt-7 focus:outline-none sm:px-8 lg:px-10 lg:py-10 xl:px-14">{children}</main>
    </div>
  );
}

function routeLabelKey(pathname: string): string {
  if (pathname.startsWith("/history")) return "nav.history";
  if (pathname.startsWith("/trends")) return "nav.trends";
  if (pathname.startsWith("/therapies")) return "therapy.title";
  if (pathname.startsWith("/preferences")) return "nav.preferences";
  return "nav.overview";
}
